/*
  Footwear PDP system — shared behavior.
  Reusable across every ADV TRIBE footwear product. No product-specific logic here —
  every element reads its data from the markup (data attributes, native form state).
  Every custom element degrades to working native HTML (radio inputs, <form>)
  when JavaScript is unavailable.
*/

class FootwearGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
    this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.#goTo(index));
    });
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.#goTo(index));
    });

    if (this.track) {
      this.track.addEventListener('scroll', this.#handleScroll.bind(this), { passive: true });
    }
  }

  #goTo(index) {
    const slide = this.slides[index];
    if (!slide || !this.track) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    this.#setActive(index);
  }

  #handleScroll() {
    if (this.#scrollTimer) clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const index = this.slides.findIndex(
        (slide) => Math.abs(slide.offsetLeft - this.track.scrollLeft) < slide.offsetWidth / 2,
      );
      if (index >= 0) this.#setActive(index);
    }, 80);
  }

  #scrollTimer = null;

  #setActive(index) {
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));
    this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
  }
}

class FootwearSizePicker extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('[data-size-option]'));
    this.stockLabel = this.querySelector('[data-size-stock-label]');
    this.stockDot = this.querySelector('[data-size-stock-dot]');
    this.sizeLabel = this.querySelectorAll('[data-selected-size-label]');
    this.priceLabels = document.querySelectorAll('[data-footwear-price]');
    this.compareLabels = document.querySelectorAll('[data-footwear-compare-price]');
    this.atcButtons = document.querySelectorAll('[data-footwear-atc]');

    this.options.forEach((option) => {
      option.addEventListener('change', () => this.#onSelect(option));
    });

    const checked = this.options.find((option) => option.checked);
    if (checked) this.#onSelect(checked, { silent: true });
  }

  #onSelect(option, { silent = false } = {}) {
    const variantId = option.value;
    const available = option.dataset.available === 'true';
    const price = option.dataset.price;
    const comparePrice = option.dataset.comparePrice;
    const label = option.dataset.label;

    // Only the sticky-ATC bar's own hidden id field needs syncing here — it lives in a
    // separate <form> from these radios. The radios themselves are already the buy-box
    // form's "id" field via native radio checked/value semantics; rewriting their .value
    // here would corrupt every unselected radio's real variant id.
    document.querySelectorAll('[data-sync-variant-id]').forEach((input) => {
      input.value = variantId;
    });

    this.sizeLabel.forEach((el) => { el.textContent = label; });

    if (this.stockLabel) {
      this.stockLabel.textContent = available
        ? this.stockLabel.dataset.inStockText
        : this.stockLabel.dataset.outOfStockText;
    }
    this.stockDot?.classList.toggle('footwear-size-picker__stock-dot--out', !available);

    // variant.price | money can legitimately return HTML (this store's money format
    // wraps the amount in a <span>), so these must use innerHTML, not textContent.
    this.priceLabels.forEach((el) => { if (price) el.innerHTML = price; });
    this.compareLabels.forEach((el) => {
      if (comparePrice) {
        el.innerHTML = comparePrice;
        el.hidden = false;
      } else {
        el.hidden = true;
      }
    });

    this.atcButtons.forEach((button) => {
      button.disabled = !available;
      button.textContent = available
        ? button.dataset.availableText
        : button.dataset.soldOutText;
    });

    if (!silent) {
      this.dispatchEvent(new CustomEvent('footwear:size-selected', {
        bubbles: true,
        detail: { variantId, available, label },
      }));
    }
  }
}

class FootwearStickyAtc extends HTMLElement {
  connectedCallback() {
    this.observedTarget = document.querySelector(this.getAttribute('data-observe-selector') || '');
    if (!this.observedTarget) {
      this.dataset.visible = 'true';
      return;
    }

    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = String(!entry.isIntersecting);
      },
      { rootMargin: '0px 0px -20% 0px' },
    );
    this.observer.observe(this.observedTarget);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

class FootwearAnnotatedList extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-annotated-row]'));
    this.hotspots = Array.from(document.querySelectorAll(`[data-hotspot-for="${this.id}"] [data-hotspot]`));

    this.rows.forEach((row, index) => {
      row.addEventListener('click', () => this.#activate(index));
    });
    this.hotspots.forEach((hotspot, index) => {
      hotspot.addEventListener('click', () => this.#activate(index));
    });
  }

  #activate(index) {
    this.rows.forEach((row, i) => row.setAttribute('aria-current', String(i === index)));
    this.hotspots.forEach((hotspot, i) => hotspot.setAttribute('aria-current', String(i === index)));
  }
}

class FootwearGridFilter extends HTMLElement {
  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('[data-filter-value]'));
    this.grid = this.closest('.footwear-collection-grid')?.querySelector('[data-footwear-grid]');
    if (!this.grid) return;

    this.items = Array.from(this.grid.querySelectorAll('[data-footwear-grid-item]'));
    this.chips.forEach((chip) => {
      chip.addEventListener('click', () => this.#applyFilter(chip));
    });
  }

  #applyFilter(activeChip) {
    const value = activeChip.dataset.filterValue;
    this.chips.forEach((chip) => chip.classList.toggle('is-active', chip === activeChip));
    this.items.forEach((item) => {
      const matches = value === 'all' || item.dataset.productType === value;
      item.hidden = !matches;
    });
  }
}

customElements.define('footwear-gallery', FootwearGallery);
customElements.define('footwear-size-picker', FootwearSizePicker);
customElements.define('footwear-sticky-atc', FootwearStickyAtc);
customElements.define('footwear-annotated-list', FootwearAnnotatedList);
customElements.define('footwear-grid-filter', FootwearGridFilter);
